// src/components/Admin/PagosAdmin.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Paper, Tabs, Tab, Stack, Typography, Button, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Snackbar, Alert, Table, TableHead, TableRow,
    TableCell, TableBody, TablePagination, Divider, Chip
} from "@mui/material";
import { Add, Edit, Delete, Refresh } from "@mui/icons-material";
import axios from "axios";

/* ==========================
   Utiles / Config
========================== */

const API = process.env.REACT_APP_API_URL;
const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
};
const headersAuth = authHeaders; // alias por compatibilidad con el helper aportado
const ESTADOS = ["PENDIENTE", "PAGADA", "RECHAZADA"];

/** Normaliza Pago desde back (snake/camel tolerado) */
const normPago = (p) => ({
    id: p.id,
    concepto: p.concepto ?? "",
    importe: p.importe ?? null,
    fechaEmision: p.fecha_emision ?? p.fechaEmision ?? null,
    fechaVencimiento: p.fecha_vencimiento ?? p.fechaVencimiento ?? null,
    estado: (p.estado ?? "").toUpperCase(),
    colegiadoId: p.colegiado_id ?? p.colegiadoId ?? p?.colegiado?.codigo ?? p?.colegiado?.id ?? "",
    tipoPagoId: p.tipo_pago_id ?? p.tipoPagoId ?? p?.tipoPago?.id ?? p?.tipo_pago?.id ?? "",
    tipoPagoNombre: p?.tipo_pago?.nombre ?? p?.tipoPago?.nombre ?? p.tipoPagoNombre ?? null,
    iban: (p.iban ?? p.iBAN ?? p.iban_devolucion ?? "").toString().trim(),
});

/** Des-normaliza para back en snake_case y FK planas */
const denormPago = (f) => {
    const out = {
        concepto: (f.concepto ?? "").trim() || null,
        importe: f.importe !== "" ? Number(f.importe) : null,
        fecha_emision: f.fechaEmision || null,
        fecha_vencimiento: f.fechaVencimiento || null,
        estado: f.estado || "PENDIENTE",
        colegiado_id: f.colegiadoId ? Number(f.colegiadoId) : null,
        iban: (f.iban ?? "").trim() || null,
    };
    if (f.tipoPagoId) out.tipo_pago_id = Number(f.tipoPagoId);
    return out;
};

/** Normaliza TipoPago */
const normTipo = (t) => ({
    id: t.id,
    nombre: t.nombre ?? "",
    observacion: t.observacion ?? "",
});

/* ==========================
   Subcomponente: CRUD Tipos de pago
========================== */
function TiposPagoTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dlgOpen, setDlgOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ id: null, nombre: "", observacion: "" });
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

    const handleSnack = (msg, sev = "success") =>
        setSnack({ open: true, msg, sev });

    const fetchAll = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/tipos-pago`, {
                headers: authHeaders(),
            });
            setRows((data || []).map(normTipo));
        } catch (e) {
            handleSnack("Error cargando tipos de pago", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const openNew = () => {
        setEditId(null);
        setForm({ id: null, nombre: "", observacion: "" });
        setDlgOpen(true);
    };
    const openEdit = (row) => {
        setEditId(row.id);
        setForm({ ...row });
        setDlgOpen(true);
    };
    const onDelete = async (row) => {
        if (!window.confirm(`¿Eliminar tipo de pago "${row.nombre}"?`)) return;
        try {
            await axios.delete(`${API}/api/tipos-pago/${row.id}`, {
                headers: authHeaders(),
            });
            handleSnack("Tipo de pago eliminado");
            fetchAll();
        } catch (e) {
            handleSnack("No se pudo eliminar (¿en uso?)", "error");
        }
    };

    const onSave = async () => {
        try {
            if (editId) {
                await axios.put(`${API}/api/tipos-pago/${editId}`, form, {
                    headers: authHeaders(),
                });
                handleSnack("Tipo de pago actualizado");
            } else {
                await axios.post(`${API}/api/tipos-pago`, form, {
                    headers: authHeaders(),
                });
                handleSnack("Tipo de pago creado");
            }
            setDlgOpen(false);
            fetchAll();
        } catch (e) {
            handleSnack("Error guardando el tipo de pago", "error");
        }
    };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Tipos de pago</Typography>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Recargar"><IconButton onClick={fetchAll}><Refresh /></IconButton></Tooltip>
                    <Button startIcon={<Add />} variant="contained" onClick={openNew}>Nuevo</Button>
                </Stack>
            </Stack>

            <Paper variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: 80 }}>ID</TableCell>
                            <TableCell>Nombre</TableCell>
                            <TableCell>Observación</TableCell>
                            <TableCell align="right" style={{ width: 120 }}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow key={r.id} hover>
                                <TableCell>{r.id}</TableCell>
                                <TableCell>{r.nombre}</TableCell>
                                <TableCell>{r.observacion}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Editar">
                                        <IconButton onClick={() => openEdit(r)} size="small"><Edit /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar">
                                        <IconButton onClick={() => onDelete(r)} size="small" color="error"><Delete /></IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!rows.length && !loading && (
                            <TableRow><TableCell colSpan={4} align="center">Sin datos</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>

            <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editId ? "Editar tipo de pago" : "Nuevo tipo de pago"}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            label="Nombre"
                            value={form.nombre}
                            onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))}
                            fullWidth
                        />
                        <TextField
                            label="Observación"
                            value={form.observacion}
                            onChange={(e) => setForm((s) => ({ ...s, observacion: e.target.value }))}
                            fullWidth
                            helperText="Si indicas un número (ej. 58.56), lo usaremos como importe por defecto en Pagos."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDlgOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={onSave}>Guardar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
                <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.sev}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}

/* ==========================
   Subcomponente: CRUD Pagos
========================== */
function PagosTab() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [tiposPago, setTiposPago] = useState([]);
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRpp] = useState(10);

    const [filterEstado, setFilterEstado] = useState("");
    const [filterColegiado, setFilterColegiado] = useState("");
    const [filterConcepto, setFilterConcepto] = useState("");
    const [filterTipoPago, setFilterTipoPago] = useState("");
    const [desdeEmision, setDesdeEmision] = useState("");
    const [hastaEmision, setHastaEmision] = useState("");
    const [desdeVenc, setDesdeVenc] = useState("");
    const [hastaVenc, setHastaVenc] = useState("");

    const [dlgOpen, setDlgOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({
        id: null,
        concepto: "",
        importe: "",
        fechaEmision: "",
        fechaVencimiento: "",
        estado: "PENDIENTE",
        colegiadoId: "",
        tipoPagoId: "",
        iban: "",
    });

    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
    const handleSnack = (msg, sev = "success") => setSnack({ open: true, msg, sev });

    // ===== Cache de detalles de colegiado (nombre completo, etc.) =====
    const colegiadoCache = useRef(new Map()); // codigo -> {nombre, apellido_1, apellido_2, ...}
    const [, forceRender] = useState(0);
    const bump = () => forceRender((n) => n + 1);

    // Helper aportado por ti (ligeramente adaptado a este archivo)
    const fetchColegiadoDetalles = async (codigoColegiado) => {
        if (!codigoColegiado) return null;
        if (colegiadoCache.current.has(codigoColegiado))
            return colegiadoCache.current.get(codigoColegiado);

        try {
            const { data } = await axios.get(`${API}/api/admin/colegiados/${codigoColegiado}`, {
                headers: headersAuth(),
            });
            const c = {
                nombre: data?.nombre ?? "",
                apellido_1: data?.apellido_1 ?? "",
                apellido_2: data?.apellido_2 ?? "",
                localidad: data?.localidad ?? "",
                provincia: data?.provincia ?? "",
            };
            colegiadoCache.current.set(codigoColegiado, c);
            return c;
        } catch {
            colegiadoCache.current.set(codigoColegiado, null);
            return null;
        }
    };

    const getNombreCompleto = (codigo) => {
        const c = colegiadoCache.current.get(codigo);
        if (!c) return "";
        const parts = [c.nombre, c.apellido_1, c.apellido_2].map((s) => (s || "").trim()).filter(Boolean);
        return parts.join(" ");
    };

    const prefetchNombres = async (list) => {
        const ids = Array.from(new Set((list || []).map((r) => r.colegiadoId).filter(Boolean)));
        const toFetch = ids.filter((id) => !colegiadoCache.current.has(id));
        if (!toFetch.length) return;
        await Promise.allSettled(toFetch.map((id) => fetchColegiadoDetalles(id)));
        bump(); // re-render una vez actualizada la cache
    };

    const fetchTipos = async () => {
        try {
            const { data } = await axios.get(`${API}/api/tipos-pago`, { headers: authHeaders() });
            setTiposPago((data || []).map(normTipo));
        } catch (_) { /* opcional */ }
    };

    const buildParams = (pg, size) => {
        const params = { page: pg, size };
        if (filterEstado) params.estado = filterEstado;
        if (filterColegiado) params.colegiado = filterColegiado;
        if (filterConcepto) params.concepto = filterConcepto.trim();
        if (filterTipoPago) params.tipo_pago_id = filterTipoPago;
        if (desdeEmision) params.desde_emision = desdeEmision;
        if (hastaEmision) params.hasta_emision = hastaEmision;
        if (desdeVenc) params.desde_vencimiento = desdeVenc;
        if (hastaVenc) params.hasta_vencimiento = hastaVenc;
        return params;
    };

    const fetchPagos = async (pg = page, size = rowsPerPage) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/pagos/page`, {
                headers: authHeaders(),
                params: buildParams(pg, size),
            });

            const content = data?.content ?? [];
            const totalElems = data?.total_elements ?? data?.totalElements ?? 0;
            const number = data?.number ?? data?.page ?? pg;
            const sizeResp = data?.size ?? data?.page_size ?? size;

            const normalized = content.map(normPago);
            setRows(normalized);
            setTotal(totalElems);
            setPage(number);
            setRpp(sizeResp);

            // Prefetch nombres para las filas visibles
            prefetchNombres(normalized);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTipos();
        fetchPagos(0, rowsPerPage); // carga inicial
    }, []);

    const openNew = () => {
        setEditId(null);
        setForm({
            id: null,
            concepto: "",
            importe: "",
            fechaEmision: "",
            fechaVencimiento: "",
            estado: "PENDIENTE",
            colegiadoId: "",
            tipoPagoId: "",
            iban: "",
        });
        setDlgOpen(true);
    };
    const openEdit = (row) => {
        setEditId(row.id);
        setForm({ ...row, importe: row.importe ?? "" });
        setDlgOpen(true);
        // asegurar que el nombre está en cache para el dialog
        if (row.colegiadoId) fetchColegiadoDetalles(row.colegiadoId).then(bump);
    };
    const onDelete = async (row) => {
        if (!window.confirm(`¿Eliminar pago #${row.id}?`)) return;
        try {
            await axios.delete(`${API}/api/pagos/${row.id}`, { headers: authHeaders() });
            handleSnack("Pago eliminado");
            fetchPagos(page, rowsPerPage);
        } catch (e) {
            handleSnack("No se pudo eliminar el pago", "error");
        }
    };

    const onSave = async () => {
        if (!form.colegiadoId) return handleSnack("Indica el colegiado", "warning");
        if (form.importe === "" || form.importe === null) return handleSnack("Indica el importe", "warning");

        const payload = denormPago(form);

        try {
            if (editId) {
                await axios.put(`${API}/api/pagos/${editId}`, payload, { headers: authHeaders() });
                handleSnack("Pago actualizado");
            } else {
                await axios.post(`${API}/api/pagos`, payload, { headers: authHeaders() });
                handleSnack("Pago creado");
            }
            setDlgOpen(false);
            fetchPagos(page, rowsPerPage);
        } catch (e) {
            handleSnack("Error guardando el pago", "error");
        }
    };

    // ===== Exportación a CSV (Excel) de Pagos =====
    const toCsvPagos = (list) => {
        const header = [
            "ID", "Colegiado", "Nombre completo", "Concepto", "Importe",
            "Fecha emisión", "Fecha vencimiento", "Estado", "Tipo de pago", "IBAN devolución"
        ];
        const lines = (list || []).map((r) => {
            const tipoNombre =
                r.tipoPagoNombre ||
                tiposPago.find(t => Number(t.id) === Number(r.tipoPagoId))?.nombre ||
                (r.tipoPagoId || "");

            const nombreCompleto = getNombreCompleto(r.colegiadoId) || "";

            const vals = [
                r.id ?? "",
                r.colegiadoId ?? "",
                nombreCompleto,
                r.concepto ?? "",
                r.importe != null && r.importe !== "" ? Number(r.importe).toFixed(2) : "",
                r.fechaEmision || "",
                r.fechaVencimiento || "",
                r.estado || "",
                tipoNombre || "",
                r.iban || "",
            ];

            return vals.map(v => `"${String(v).replace(/"/g, '""')}"`).join(";");
        });
        // BOM para Excel UTF-8
        return "\uFEFF" + [header.join(";"), ...lines].join("\n");
    };

    const exportPagos = async () => {
        const pageSizeBulk = 1000;
        let pg = 0;
        let totalElems = Infinity;
        let acc = [];
        try {
            while (acc.length < totalElems) {
                const params = buildParams(pg, pageSizeBulk);
                const { data } = await axios.get(`${API}/api/pagos/page`, {
                    headers: authHeaders(),
                    params,
                });
                const content = data?.content ?? [];
                const tot = data?.total_elements ?? data?.totalElements ?? content.length;

                acc = acc.concat(content);
                totalElems = tot;

                if (content.length < pageSizeBulk) break;
                pg += 1;
            }
        } catch (e) {
            handleSnack("No se pudo exportar los pagos", "error");
            return;
        }

        // Asegurar nombres para exportación (prefetch sobre el acumulado)
        const normalized = acc.map(normPago);
        await prefetchNombres(normalized);

        const csv = toCsvPagos(normalized);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "pagos.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    const mostrarColumnaIban = useMemo(
        () => rows.some((r) => (r.iban || "").trim() !== ""),
        [rows]
    );

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Pagos</Typography>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Exportar a Excel">
                        <Button variant="outlined" onClick={exportPagos}>Exportar</Button>
                    </Tooltip>
                    <Tooltip title="Recargar">
                        <IconButton onClick={() => fetchPagos(page, rowsPerPage)}><Refresh /></IconButton>
                    </Tooltip>
                    <Button startIcon={<Add />} variant="contained" onClick={openNew}>Nuevo</Button>
                </Stack>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack spacing={2}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <TextField
                            label="Colegiado"
                            value={filterColegiado}
                            onChange={(e) => setFilterColegiado(e.target.value)}
                            sx={{ minWidth: 160 }}
                            helperText="Filtra por código/ID de colegiado"
                        />
                        <TextField
                            label="Concepto"
                            value={filterConcepto}
                            onChange={(e) => setFilterConcepto(e.target.value)}
                            sx={{ minWidth: 220 }}
                        />
                        <TextField
                            select label="Estado" value={filterEstado}
                            onChange={(e) => setFilterEstado(e.target.value)} sx={{ minWidth: 180 }}
                        >
                            <MenuItem value="">(Todos)</MenuItem>
                            {ESTADOS.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                        </TextField>
                        <TextField
                            select label="Tipo de pago" value={filterTipoPago}
                            onChange={(e) => setFilterTipoPago(e.target.value)} sx={{ minWidth: 220 }}
                        >
                            <MenuItem value="">(Todos)</MenuItem>
                            {tiposPago.map(t => <MenuItem key={t.id} value={t.id}>{t.nombre}</MenuItem>)}
                        </TextField>
                    </Stack>

                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <TextField type="date" label="Desde emisión" InputLabelProps={{ shrink: true }}
                            value={desdeEmision} onChange={(e) => setDesdeEmision(e.target.value)} />
                        <TextField type="date" label="Hasta emisión" InputLabelProps={{ shrink: true }}
                            value={hastaEmision} onChange={(e) => setHastaEmision(e.target.value)} />
                        <TextField type="date" label="Desde venc." InputLabelProps={{ shrink: true }}
                            value={desdeVenc} onChange={(e) => setDesdeVenc(e.target.value)} />
                        <TextField type="date" label="Hasta venc." InputLabelProps={{ shrink: true }}
                            value={hastaVenc} onChange={(e) => setHastaVenc(e.target.value)} />
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button variant="outlined" onClick={() => fetchPagos(0, rowsPerPage)}>Aplicar</Button>
                            <Button onClick={() => {
                                setFilterEstado("");
                                setFilterColegiado("");
                                setFilterConcepto("");
                                setFilterTipoPago("");
                                setDesdeEmision(""); setHastaEmision("");
                                setDesdeVenc(""); setHastaVenc("");
                                fetchPagos(0, rowsPerPage);
                            }}>Limpiar</Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Paper>

            <Paper variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: 80 }}>ID</TableCell>
                            <TableCell style={{ width: 240 }}>Colegiado / Nombre</TableCell>
                            <TableCell>Concepto</TableCell>
                            <TableCell align="right" style={{ width: 120 }}>Importe</TableCell>
                            <TableCell style={{ width: 140 }}>F. Emisión</TableCell>
                            <TableCell style={{ width: 160 }}>F. Vencimiento</TableCell>
                            <TableCell style={{ width: 140 }}>Estado</TableCell>
                            <TableCell style={{ width: 160 }}>Tipo pago</TableCell>
                            {mostrarColumnaIban && <TableCell style={{ width: 220 }}>IBAN devolución</TableCell>}
                            <TableCell align="right" style={{ width: 120 }}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r) => {
                            const nombreCompleto = getNombreCompleto(r.colegiadoId);
                            return (
                                <TableRow key={r.id} hover>
                                    <TableCell>{r.id}</TableCell>
                                    <TableCell>
                                        <Stack spacing={0.25}>
                                            <Typography variant="body2">{r.colegiadoId || "-"}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {r.colegiadoId
                                                    ? (nombreCompleto || "Cargando…")
                                                    : "-"}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{r.concepto || "-"}</TableCell>
                                    <TableCell align="right">
                                        {r.importe != null && r.importe !== "" ? Number(r.importe).toFixed(2) : "-"}
                                    </TableCell>
                                    <TableCell>{r.fechaEmision || "-"}</TableCell>
                                    <TableCell>{r.fechaVencimiento || "-"}</TableCell>
                                    <TableCell>
                                        {r.estado ? (
                                            <Chip
                                                size="small"
                                                label={r.estado}
                                                color={r.estado === "PAGADA" ? "success" : r.estado === "RECHAZADA" ? "error" : "warning"}
                                            />
                                        ) : "-"}
                                    </TableCell>
                                    <TableCell>
                                        {r.tipoPagoNombre
                                            ? r.tipoPagoNombre
                                            : r.tipoPagoId
                                                ? (tiposPago.find(t => Number(t.id) === Number(r.tipoPagoId))?.nombre || r.tipoPagoId)
                                                : "-"}
                                    </TableCell>

                                    {mostrarColumnaIban && (
                                        <TableCell>{r.iban ? r.iban : "-"}</TableCell>
                                    )}

                                    <TableCell align="right">
                                        <Tooltip title="Editar">
                                            <IconButton size="small" onClick={() => openEdit(r)}><Edit /></IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar">
                                            <IconButton size="small" color="error" onClick={() => onDelete(r)}><Delete /></IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {!rows.length && !loading && (
                            <TableRow>
                                <TableCell colSpan={mostrarColumnaIban ? 10 : 9} align="center">
                                    Sin datos
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(_, newPage) => fetchPagos(newPage, rowsPerPage)}
                    onRowsPerPageChange={(e) => {
                        const newSize = parseInt(e.target.value, 10);
                        fetchPagos(0, newSize);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Paper>

            <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{editId ? "Editar pago" : "Nuevo pago"}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} mt={1}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    label="Colegiado (código/ID)"
                                    value={form.colegiadoId}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setForm(s => ({ ...s, colegiadoId: val }));
                                    }}
                                    onBlur={async () => {
                                        if (form.colegiadoId) {
                                            await fetchColegiadoDetalles(form.colegiadoId);
                                            bump();
                                        }
                                    }}
                                    fullWidth
                                />
                                {form.colegiadoId ? (
                                    <Typography variant="caption" color="text.secondary">
                                        {getNombreCompleto(form.colegiadoId) || "Cargando…"}
                                    </Typography>
                                ) : null}
                            </Box>
                            <TextField
                                select label="Estado" value={form.estado}
                                onChange={(e) => setForm(s => ({ ...s, estado: e.target.value }))}
                                sx={{ minWidth: 180 }}
                            >
                                {ESTADOS.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                            </TextField>
                            <TextField
                                select
                                label="Tipo de pago (opcional)"
                                value={form.tipoPagoId || ""}
                                onChange={(e) => {
                                    const idSel = e.target.value;
                                    const tipo = tiposPago.find(t => Number(t.id) === Number(idSel));
                                    let nuevoImporte = form.importe;
                                    // Si observacion es numérica, usarla como importe por defecto
                                    if (tipo && !Number.isNaN(parseFloat(tipo.observacion))) {
                                        nuevoImporte = parseFloat(tipo.observacion);
                                    }
                                    setForm(s => ({ ...s, tipoPagoId: idSel, importe: nuevoImporte }));
                                }}
                                sx={{ minWidth: 220 }}
                            >
                                <MenuItem value="">(Sin tipo)</MenuItem>
                                {tiposPago.map(t => <MenuItem key={t.id} value={t.id}>{t.nombre}</MenuItem>)}
                            </TextField>
                        </Stack>

                        <TextField
                            label="Concepto"
                            value={form.concepto}
                            onChange={(e) => setForm(s => ({ ...s, concepto: e.target.value }))}
                            fullWidth
                        />

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                type="number" inputMode="decimal"
                                label="Importe"
                                value={form.importe}
                                onChange={(e) => setForm(s => ({ ...s, importe: e.target.value }))}
                            />
                            <TextField
                                type="date" label="Fecha emisión" InputLabelProps={{ shrink: true }}
                                value={form.fechaEmision || ""}
                                onChange={(e) => setForm(s => ({ ...s, fechaEmision: e.target.value }))}
                            />
                            <TextField
                                type="date" label="Fecha vencimiento" InputLabelProps={{ shrink: true }}
                                value={form.fechaVencimiento || ""}
                                onChange={(e) => setForm(s => ({ ...s, fechaVencimiento: e.target.value }))}
                            />
                        </Stack>

                        <TextField
                            label="IBAN devolución"
                            value={form.iban || ""}
                            onChange={(e) => setForm(s => ({ ...s, iban: e.target.value }))}
                            fullWidth
                            helperText="Si este pago tiene un IBAN específico para devoluciones, aparecerá aquí."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDlgOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={onSave}>Guardar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
                <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.sev}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}

/* ==========================
   Componente principal
========================== */
export default function PagosAdmin() {
    const [tab, setTab] = useState(0);

    return (
        <Box>
            <Paper sx={{ mb: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
                    <Tab label="Pagos" />
                    <Tab label="Tipos de pago" />
                </Tabs>
            </Paper>

            {tab === 0 && <PagosTab />}
            {tab === 1 && <TiposPagoTab />}

            <Divider sx={{ my: 3 }} />
            <Typography variant="body2" color="text.secondary">
                Nota: al crear/actualizar pagos, la aplicación envía avisos al colegiado según el estado.
            </Typography>
        </Box>
    );
}
